import React, { useState, useEffect } from 'react';
import Card from '../../components/ui/Card';
import Lightbox from '../../components/ui/Lightbox';
import { getCleanImageUrl } from '../../utils/imageHelper';
import {
  CalendarIcon,
  PhotoIcon,
  MagnifyingGlassPlusIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

export default function GaleriePage() {
  const [galerieData, setGalerieData] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedImgInfo, setSelectedImgInfo] = useState(null);

  useEffect(() => {
    fetch('/galerie_acem_fes/galerieData.json')
      .then((res) => res.json())
      .then((data) => setGalerieData(data))
      .catch((err) => console.error("Erreur de chargement de la galerie:", err));
  }, []);

  // Global hero slides (flatten all images)
  const allHeroSlides = React.useMemo(() => {
    const slides = [];
    galerieData.forEach((section) => {
      if (section.images && section.images.length > 0) {
        slides.push({
          src: getCleanImageUrl(section.images[0]),
          title: section.titre || section.title || "Événement ACEM Fès",
          date: section.date,
        });
      }
    });
    return slides;
  }, [galerieData]);

  // Autoplay hero slider
  useEffect(() => {
    if (allHeroSlides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % allHeroSlides.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [allHeroSlides.length]);

  return (
    <div className="max-w-7xl mx-auto space-y-12 animate-fade-in px-4 py-4">
      {/* Header informatif */}
      <div className="text-center space-y-3 max-w-2xl mx-auto pt-2">

        <h1 className="font-title text-2xl sm:text-3xl md:text-4xl font-extrabold text-acem-primary-dark">
          Moments Forts & Événements
        </h1>
        <p className="text-sm sm:text-base text-acem-muted max-w-xl mx-auto leading-relaxed">
          Revivez en images les grands rendez-vous culturels, académiques et sportifs de l'ACEM Fès.
        </p>
      </div>

      {/* Hero Slider */}
      {allHeroSlides.length > 0 && (
        <div className="relative w-full h-[320px] sm:h-[420px] md:h-[480px] rounded-2xl md:rounded-3xl overflow-hidden shadow-acem-lg bg-acem-surface-container-low border border-acem-border/60">
          {allHeroSlides.map((slide, idx) => {
            const isActive = idx === currentSlide;
            return (
              <div
                key={idx}
                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${isActive ? "opacity-100 z-10 pointer-events-auto" : "opacity-0 pointer-events-none"
                  }`}
              >
                <img
                  src={slide.src}
                  alt={slide.title}
                  className="w-full h-full object-cover object-center"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent flex flex-col justify-end p-6 sm:p-10 text-white">
                  <span className="text-xs sm:text-sm font-bold uppercase tracking-widest text-acem-accent mb-1.5 flex items-center gap-1.5">
                    <CalendarIcon className="w-4 h-4 text-acem-accent" />
                    {slide.date}
                  </span>
                  <h2 className="font-title text-white text-2xl sm:text-4xl font-extrabold drop-shadow-md">
                    {slide.title}
                  </h2>
                </div>
              </div>
            );
          })}

          {/* Slider indicators */}
          <div className="absolute bottom-4 right-6 z-20 flex gap-2">
            {allHeroSlides.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setCurrentSlide(idx)}
                className={`h-2.5 rounded-full transition-all duration-300 ${idx === currentSlide ? "w-7 bg-acem-accent shadow-md" : "w-2.5 bg-white/50 hover:bg-white/80"
                  }`}
                aria-label={`Slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Sections d'activités */}
      {galerieData.map((section, secIdx) => {
        const secTitle = section.titre || section.title || "Événement ACEM Fès";
        return (
          <section key={secIdx} className="space-y-4 pt-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-acem-border pb-3">
              <div className="flex items-center gap-2">
                <PhotoIcon className="w-5 h-5 text-acem-primary flex-shrink-0" />
                <h3 className="font-title text-xl sm:text-2xl font-bold text-acem-primary-dark">
                  {secTitle}
                </h3>
              </div>
              <span className="text-xs sm:text-sm font-bold text-acem-muted flex items-center gap-1.5 bg-acem-surface-container px-3 py-1 rounded-full border border-acem-border/50">
                <CalendarIcon className="w-4 h-4 text-acem-primary" />
                {section.date}
              </span>
            </div>

            <p className="text-xs sm:text-sm text-acem-text/80 leading-relaxed max-w-3xl">
              {section.description}
            </p>

            {/* Horizontal scrollable carousel */}
            <div className="flex gap-4 overflow-x-auto pb-4 pt-1 snap-x scrollbar-thin">
              {section.images?.map((img, imgIdx) => {
                const cleanImg = getCleanImageUrl(img);
                return (
                  <div
                    key={imgIdx}
                    onClick={() =>
                      setSelectedImgInfo({
                        src: cleanImg,
                        gallery: section.images.map(getCleanImageUrl),
                        index: imgIdx,
                        title: secTitle,
                      })
                    }
                    className="flex-none w-60 sm:w-72 h-44 sm:h-52 rounded-2xl overflow-hidden shadow-acem-sm hover:shadow-acem-xl cursor-pointer border border-acem-border group snap-start bg-acem-surface-container-low relative transition-all duration-300 hover:-translate-y-1"
                  >
                    <img
                      src={cleanImg}
                      alt={`${secTitle} - photo ${imgIdx + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "/logo.png";
                        e.currentTarget.className = "w-16 h-16 object-contain m-auto opacity-40";
                      }}
                    />
                    {/* Surcouche visuelle au survol avec vraie icône vectorielle */}
                    <div className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-[2px]">
                      <span className="px-4 py-2 rounded-full bg-white/95 text-acem-primary-dark font-bold text-xs shadow-xl flex items-center gap-2 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                        <MagnifyingGlassPlusIcon className="w-4 h-4 text-acem-primary" />
                        Agrandir
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      {/* Social Invitation CTA */}
      <Card variant="glass" className="p-8 sm:p-12 text-center relative overflow-hidden space-y-4 max-w-3xl mx-auto shadow-acem-md">
        <div className="w-12 h-12 rounded-full bg-acem-primary/10 text-acem-primary flex items-center justify-center mx-auto mb-2">
          <SparklesIcon className="w-6 h-6 text-acem-primary" />
        </div>
        <h3 className="font-title text-2xl sm:text-3xl font-extrabold text-acem-primary-dark">
          Rejoignez la communauté <span className="text-acem-primary">ACEM Fès</span>
        </h3>
        <p className="text-xs sm:text-sm text-acem-muted max-w-lg mx-auto leading-relaxed">
          Ne manquez aucun événement ! Suivez-nous sur nos réseaux sociaux pour découvrir nos albums complets et nos activités en direct.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
          <a
            href="https://www.facebook.com/share/1anbGqXBfc/?mibextid=wwXIfr"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#1877F2] text-white font-bold text-sm shadow-md hover:bg-[#166fe5] transition-all hover:scale-105"
          >
            Facebook
          </a>
          <a
            href="https://www.instagram.com/acem_fes?igsh=MW0yeGswZWtxZGM2Yw=="
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-amber-500 via-pink-500 to-purple-600 text-white font-bold text-sm shadow-md hover:opacity-95 transition-all hover:scale-105"
          >
            Instagram
          </a>
        </div>
      </Card>

      {/* Lightbox for gallery images */}
      {selectedImgInfo && (
        <Lightbox
          src={selectedImgInfo.src}
          alt={selectedImgInfo.title}
          gallery={selectedImgInfo.gallery}
          currentIndex={selectedImgInfo.index}
          onNavigate={(newIdx) =>
            setSelectedImgInfo((prev) => ({
              ...prev,
              src: prev.gallery[newIdx],
              index: newIdx,
            }))
          }
          onClose={() => setSelectedImgInfo(null)}
        />
      )}
    </div>
  );
}
